import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  HomeIcon,
  BookOpenIcon,
  HeartIcon,
  ShoppingBagIcon,
  ChatBubbleLeftIcon,
  UserIcon,
  Cog6ToothIcon,
  SparklesIcon,
  TrophyIcon,
  FireIcon
} from '@heroicons/react/24/outline'

const Sidebar: React.FC = () => {
  const location = useLocation()

  const isActive = (path: string) => location.pathname === path

  const mainNavItems = [
    { to: '/', icon: HomeIcon, text: 'Home', badge: null },
    { to: '/stories', icon: BookOpenIcon, text: 'Stories', badge: '12' },
    { to: '/dating', icon: HeartIcon, text: 'Dating', badge: '3' },
    { to: '/shop', icon: ShoppingBagIcon, text: 'Shop', badge: 'New' },
    { to: '/chat', icon: ChatBubbleLeftIcon, text: 'Messages', badge: '5' },
  ]

  const secondaryNavItems = [
    { to: '/profile', icon: UserIcon, text: 'Profile', badge: null },
    { to: '/premium', icon: SparklesIcon, text: 'Premium', badge: null },
    { to: '/achievements', icon: TrophyIcon, text: 'Achievements', badge: null },
    { to: '/settings', icon: Cog6ToothIcon, text: 'Settings', badge: null },
  ]

  return (
    <aside className="fixed left-0 top-16 z-40 h-[calc(100vh-4rem)] w-64 transform bg-dark-100/50 backdrop-blur-xl border-r border-white/10 transition-transform duration-300 ease-in-out lg:translate-x-0 -translate-x-full">
      <div className="flex h-full flex-col">
        {/* Premium Banner */}
        <div className="p-4">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="card-glass p-4 bg-gradient-to-r from-primary-600/20 to-secondary-600/20"
          >
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <FireIcon className="h-8 w-8 text-accent-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white">Go Premium</p>
                <p className="text-xs text-white/70">Unlock exclusive features</p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary w-full mt-3 text-sm py-2"
            >
              Upgrade Now
            </motion.button>
          </motion.div>
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 space-y-1 px-4">
          <div className="space-y-1">
            {mainNavItems.map((item) => (
              <SidebarLink
                key={item.to}
                to={item.to}
                icon={item.icon}
                text={item.text}
                badge={item.badge}
                isActive={isActive(item.to)}
              />
            ))}
          </div>

          <div className="pt-6">
            <h3 className="px-3 text-xs font-semibold text-white/50 uppercase tracking-wider">
              Account
            </h3>
            <div className="mt-2 space-y-1">
              {secondaryNavItems.map((item) => (
                <SidebarLink
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  text={item.text}
                  badge={item.badge}
                  isActive={isActive(item.to)}
                />
              ))}
            </div>
          </div>
        </nav>

        {/* User Stats */}
        <div className="p-4 border-t border-white/10">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xl font-bold text-primary-400">1.2k</p>
              <p className="text-xs text-white/50">Followers</p>
            </div>
            <div>
              <p className="text-xl font-bold text-secondary-400">856</p>
              <p className="text-xs text-white/50">Following</p>
            </div>
            <div>
              <p className="text-xl font-bold text-accent-400">42</p>
              <p className="text-xs text-white/50">Stories</p>
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}

interface SidebarLinkProps {
  to: string
  icon: React.ComponentType<any>
  text: string
  badge?: string | null
  isActive?: boolean
}

const SidebarLink: React.FC<SidebarLinkProps> = ({ to, icon: Icon, text, badge, isActive }) => (
  <Link to={to}>
    <motion.div
      whileHover={{ scale: 1.02, x: 4 }}
      whileTap={{ scale: 0.98 }}
      className={`group flex items-center px-3 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${
        isActive
          ? 'bg-gradient-to-r from-primary-600/30 to-secondary-600/30 text-white border border-primary-500/20'
          : 'text-white/70 hover:text-white hover:bg-white/5'
      }`}
    >
      <Icon
        className={`mr-3 h-5 w-5 flex-shrink-0 transition-colors ${
          isActive ? 'text-primary-400' : 'text-white/50 group-hover:text-white/70'
        }`}
      />
      <span className="flex-1">{text}</span>
      {badge && (
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            badge === 'New'
              ? 'bg-accent-500 text-white'
              : 'bg-primary-500/20 text-primary-300'
          }`}
        >
          {badge}
        </motion.span>
      )}
    </motion.div>
  </Link>
)

export default Sidebar